import { ChangeDetectionStrategy, Component, EventEmitter, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { MessageService } from 'primeng/api';
import { WatchlistService } from '../../../../core/services/watchlist.service';
import { LoggingService } from '../../../../core/services/logging.service';
import { Watchlist } from '../../../../shared/models/watchlist.model';

@Component({
  selector: 'app-watchlist-create-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    TextareaModule
  ],
  templateUrl: './watchlist-create-dialog.component.html',
  styleUrl: './watchlist-create-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WatchlistCreateDialogComponent {
  private watchlistService = inject(WatchlistService);
  private messageService = inject(MessageService);
  private logger = inject(LoggingService);

  @Output() watchlistCreated = new EventEmitter<Watchlist>();

  visible = signal(false);
  isLoading = signal(false);
  name = signal('');
  description = signal('');

  /**
   * Show the dialog
   */
  show(): void {
    this.resetForm();
    this.visible.set(true);
  }

  /**
   * Hide the dialog
   */
  hide(): void {
    this.visible.set(false);
    this.resetForm();
  }

  /**
   * Reset form fields
   */
  private resetForm(): void {
    this.name.set('');
    this.description.set('');
  }

  /**
   * Check if form is valid
   */
  isFormValid(): boolean {
    return this.name().trim().length > 0;
  }

  /**
   * Handle name input change
   */
  onNameChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.name.set(input.value);
  }

  /**
   * Handle description input change
   */
  onDescriptionChange(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.description.set(textarea.value);
  }

  /**
   * Create a new watchlist
   */
  createWatchlist(): void {
    if (!this.isFormValid()) {
      return;
    }

    this.isLoading.set(true);

    this.watchlistService
      .createWatchlist(this.name().trim(), this.description().trim() || undefined)
      .subscribe({
        next: (watchlist) => {
          if (watchlist) {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Watchlist created successfully'
            });
            this.watchlistCreated.emit(watchlist);
            this.hide();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to create watchlist'
            });
          }
          this.isLoading.set(false);
        },
        error: (error) => {
          this.logger.error('Error creating watchlist:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create watchlist'
          });
          this.isLoading.set(false);
        }
      });
  }
}
